import React from 'react';
import { AlertTriangle, ChevronRight, ChevronLeft, X } from 'lucide-react';
import { PhaseAtcCard } from './PhaseNotes';

/**
 * AbnormalProcedurePanel - "Abnormal Procedures" section inside the
 * In-Flight Assistance panel (added 2026-10-15). Banner -> list of 13
 * QRC cards -> detail view, all rendered within the existing Assistance
 * panel/overlay (same access gating as the rest of InstructorPanel.jsx).
 */

export const CATEGORY_COLORS = {
  MEMORY: '#F87171',
  EMERGENCY: '#F87171',
  ABNORMAL: '#F5B041',
  PROCEDURE: '#4FD1F5',
};

const CategoryChip = ({ category }) => {
  const color = CATEGORY_COLORS[category] || 'var(--gc-text-muted)';
  return (
    <span
      className="font-plex-mono text-[10px] font-semibold px-2 py-0.5 rounded-gc-badge border uppercase tracking-wider shrink-0 w-fit"
      style={{ color, borderColor: color }}
      data-testid="abnormal-procedure-category-chip"
    >
      {category}
    </span>
  );
};

/** Generic full-width banner card used by every In-Flight Assistance
 * sub-section (Abnormal Procedures, MEL, ...) - same component, same
 * styling, parameterised by icon/accent colour/copy. */
export const AssistanceSectionBanner = ({ icon: Icon, accentColor, title, subtitle, onClick, testId }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 min-h-[64px] px-3.5 rounded-gc-checklist border mb-3 text-left transition-colors hover:opacity-90"
    style={{ borderColor: 'var(--gc-border-card)', borderLeft: `3px solid ${accentColor}`, background: 'var(--gc-card)' }}
    data-testid={testId}
  >
    <div className="w-10 h-10 rounded-gc-button flex items-center justify-center shrink-0" style={{ background: `${accentColor}1F` }}>
      <Icon className="w-5 h-5" style={{ color: accentColor }} strokeWidth={1.8} />
    </div>
    <div className="flex-1 min-w-0 flex flex-col">
      <span className="font-barlow-condensed font-semibold text-[18px] truncate" style={{ color: 'var(--gc-text-main)' }}>
        {title}
      </span>
      <span className="font-plex-mono text-[11px] truncate" style={{ color: 'var(--gc-text-muted)' }}>
        {subtitle}
      </span>
    </div>
    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--gc-text-faint)' }} />
  </button>
);

export const AbnormalProceduresBanner = ({ onClick }) => (
  <AssistanceSectionBanner
    icon={AlertTriangle}
    accentColor="#F5B041"
    title="Abnormal Procedures"
    subtitle="Engine failure, fire, descent, windshear and more, with ATC calls"
    onClick={onClick}
    testId="abnormal-procedures-banner"
  />
);

export const normalizeSearchText = (value) => (value || '').toString().replace(/\s+/g, '').toLowerCase();

/** True if a procedure matches a normalized query on title/subtitle text
 * or its QRC number - e.g. "QRC 2", "QRC 02", "qrc2" and "qrc 02" all
 * match procedure number 2 (added 2026-10-16, leading zero optional). */
const procedureMatchesQuery = (proc, query) => {
  if (normalizeSearchText(proc.title).includes(query) || normalizeSearchText(proc.subtitle).includes(query)) {
    return true;
  }
  const match = query.match(/^qrc0*(\d+)$/);
  return !!match && proc.number === parseInt(match[1], 10);
};

export const AbnormalProceduresList = ({ procedures, onSelect, onBack, searchQuery }) => {
  const sorted = [...(procedures || [])].sort((a, b) => a.number - b.number);
  const query = normalizeSearchText(searchQuery);
  const filtered = query
    ? sorted.filter((p) => procedureMatchesQuery(p, query))
    : sorted;
  return (
    <div data-testid="abnormal-procedures-list">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 mb-3 font-plex-mono text-xs w-fit"
        style={{ color: 'var(--gc-cyan)' }}
        data-testid="abnormal-procedures-back-btn"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> BACK
      </button>
      <div className="-mx-1 px-1 space-y-1">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--gc-text-faint)' }} data-testid="abnormal-procedures-no-results">
            No procedures match "{searchQuery}"
          </div>
        ) : filtered.map((proc, index) => (
          <button
            key={proc.id}
            onClick={() => onSelect(proc)}
            className="w-full p-3 text-left rounded-gc-card transition-colors hover:opacity-90"
            style={{ background: 'var(--gc-card)' }}
            data-testid={`abnormal-procedure-row-${index}`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-plex-mono text-[11px] font-semibold" style={{ color: 'var(--gc-text-muted)' }}>
                QRC {String(proc.number).padStart(2, '0')}
              </span>
              <CategoryChip category={proc.category} />
            </div>
            <p className="font-barlow-condensed font-bold text-[15px]" style={{ color: 'var(--gc-text-main)' }}>
              {proc.title}
            </p>
            <p className="font-plex-mono text-[11px] mt-0.5" style={{ color: 'var(--gc-text-muted)' }}>
              {proc.subtitle}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

const StepRow = ({ number, step }) => (
  <div className="flex flex-col gap-0.5" data-testid={`abnormal-step-${number}`}>
    <div className="flex items-start gap-2">
      <span className="font-plex-mono text-[10px] font-semibold shrink-0 mt-[3px]" style={{ color: 'var(--gc-text-faint)' }}>
        {String(number).padStart(2, '0')}
      </span>
      <div className="flex-1 flex items-baseline gap-2 min-w-0">
        <span className="text-sm shrink-0" style={{ color: 'var(--gc-text-main)' }}>{step.item}</span>
        <span className="flex-1 border-b border-dotted self-end mb-[3px]" style={{ borderColor: 'var(--gc-border-outline)', minWidth: '8px' }} />
      </div>
      <span
        className="font-plex-mono text-sm font-bold text-right shrink-0"
        style={{ color: 'var(--gc-text-main)', width: '44%', whiteSpace: 'normal' }}
      >
        {step.action}
      </span>
    </div>
    {step.where && (
      <p className="text-[11px] pl-6" style={{ color: 'var(--gc-text-muted)' }}>{step.where}</p>
    )}
  </div>
);

const renderBlocks = (blocks, categoryColor) => {
  let stepCounter = 0;
  return (blocks || []).map((block, bi) => {
    if (block.type === 'immediate' || block.type === 'steps' || block.type === 'condition') {
      return (
        <div key={bi} className="flex flex-col gap-2">
          {block.type === 'condition' ? (
            <div className="font-plex-mono text-[12px] font-semibold" style={{ color: '#F5B041' }}>
              ▸ {block.title}
            </div>
          ) : (
            <div
              className="px-3 py-1.5 rounded-gc-button font-barlow-condensed font-bold text-[13px] tracking-wide w-fit"
              style={block.type === 'immediate' ? { background: categoryColor, color: '#0A0E15' } : { background: '#0A0E15', color: '#FFFFFF' }}
            >
              {block.title}
            </div>
          )}
          <div className="flex flex-col gap-2.5">
            {block.steps.map((step, si) => {
              stepCounter += 1;
              return <StepRow key={si} number={stepCounter} step={step} />;
            })}
          </div>
        </div>
      );
    }
    if (block.type === 'note') {
      return (
        <div key={bi} className="px-3.5 py-2.5 rounded-gc-card" style={{ border: '1.5px dashed var(--gc-border-outline)' }}>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{block.text}</p>
        </div>
      );
    }
    if (block.type === 'radio') {
      return (
        <div
          key={bi}
          className="px-3.5 py-2.5 rounded-gc-card border flex flex-col gap-1.5"
          style={{ background: 'rgba(79,209,245,0.07)', borderColor: 'var(--gc-cyan)' }}
        >
          <span
            className="font-plex-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-gc-badge w-fit"
            style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
          >
            RADIO
          </span>
          {block.title && block.title !== 'RADIO' && (
            <span className="font-plex-mono text-[11px] font-semibold" style={{ color: 'var(--gc-cyan)' }}>{block.title}</span>
          )}
          <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-main)' }}>{block.text}</p>
        </div>
      );
    }
    if (block.type === 'watch') {
      return (
        <div key={bi} className="flex flex-col gap-2">
          <span className="font-plex-mono text-[11px] font-semibold tracking-wider" style={{ color: 'var(--gc-text-muted)' }}>WATCH</span>
          <div className="flex flex-wrap gap-1.5">
            {(block.items || []).map((it, ii) => (
              <span
                key={ii}
                className="font-plex-mono text-[11px] px-2 py-1 rounded-gc-badge border"
                style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
              >
                {it}
              </span>
            ))}
          </div>
        </div>
      );
    }
    if (block.type === 'table') {
      return (
        <div key={bi} className="flex flex-col gap-1.5">
          {block.title && (
            <span className="font-plex-mono text-[11px] font-semibold" style={{ color: 'var(--gc-text-muted)' }}>{block.title}</span>
          )}
          <div className="overflow-x-auto rounded-gc-card border" style={{ borderColor: 'var(--gc-border-card)' }}>
            <table className="font-plex-mono text-[11px] w-full border-collapse">
              <tbody>
                {(block.rows || []).map((row, ri) => (
                  <tr key={ri} style={ri === 0 ? { background: 'var(--gc-current-row)' } : {}}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="px-2.5 py-1.5 whitespace-nowrap border-b"
                        style={{
                          borderColor: 'var(--gc-border-row)',
                          color: ri === 0 ? 'var(--gc-cyan)' : 'var(--gc-text-body)',
                          fontWeight: ri === 0 ? 600 : 400,
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return null;
  });
};

export const AbnormalProcedureDetail = ({ procedure, onClose }) => {
  if (!procedure) return null;
  const categoryColor = CATEGORY_COLORS[procedure.category] || 'var(--gc-text-muted)';

  return (
    <div
      className="fixed inset-y-0 right-0 w-full md:w-[450px] lg:w-[500px] border-l shadow-2xl z-50 overflow-y-auto"
      style={{ background: 'var(--gc-bg)', borderColor: 'var(--gc-border-nav)' }}
      data-testid="abnormal-procedure-detail-panel"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-gc-button z-10"
        style={{ color: 'var(--gc-text-muted)' }}
        data-testid="close-abnormal-procedure-detail"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-6 md:p-8 flex flex-col gap-5">
        <div className="flex flex-col gap-2 pb-4 border-b" style={{ borderColor: 'var(--gc-border-row)' }}>
          <CategoryChip category={procedure.category} />
          <h2 className="font-barlow-condensed font-bold text-2xl" style={{ color: 'var(--gc-text-main)' }}>
            {procedure.title}
          </h2>
          <p className="font-plex-mono text-xs" style={{ color: 'var(--gc-text-muted)' }}>{procedure.subtitle}</p>
          <p className="text-[11px]" style={{ color: 'var(--gc-text-faint)' }}>{procedure.source}</p>
        </div>

        <div className="px-3.5 py-3 rounded-gc-card" style={{ background: 'var(--gc-card)', borderLeft: `3px solid ${categoryColor}` }}>
          <span className="font-plex-mono text-[10px] font-semibold tracking-wider block mb-1" style={{ color: categoryColor }}>
            YOU'LL SEE / HEAR
          </span>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{procedure.cue}</p>
        </div>

        <div className="flex flex-col gap-4">
          {renderBlocks(procedure.blocks, categoryColor)}
        </div>

        {procedure.atc && procedure.atc.length > 0 && (
          <PhaseAtcCard atc={{ id: procedure.id, title: 'Radio', exchange: procedure.atc }} />
        )}

        <p className="text-[11px] leading-relaxed pt-3 border-t" style={{ color: 'var(--gc-text-faint)', borderColor: 'var(--gc-border-row)' }}>
          Simulation use only. Based on the Airbus A320 QRH and FCOM, written for single-pilot flight in the Fenix A320.
        </p>
      </div>
    </div>
  );
};
